import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ALERT_METRICS, type AlertMetric, type AlertRule } from "@/contracts";

import {
  alertRulesQuery,
  createDefaultAlertRulesMutation,
  type AlertRuleFilters,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { SeverityBadge } from "@/features/devices/device-badges";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import { Alert } from "@/components/ui/alert";
import {
  ALERT_METRIC_LABELS,
  formatBounds,
  formatDuration,
  metricHasRuleTimers,
} from "./alert-labels";
import { CreateAlertRuleDialog, EditAlertRuleDialog } from "./edit-alert-rule-dialog";
import { TestIncidentEmailCard } from "./test-incident-email-card";
import { TestIncidentWhatsAppCard } from "./test-incident-whatsapp-card";

function metricOf(value: string): AlertMetric | undefined {
  return (ALERT_METRICS as readonly string[]).includes(value) ? (value as AlertMetric) : undefined;
}

/**
 * The rules the alert engine works from, and who hears about them.
 *
 * The screen's real job is the last column. An organization whose rules have
 * no recipients has a working alert engine that tells nobody, and that state
 * is invisible everywhere else in the app — so it is called out here rather
 * than left for somebody to notice during an incident.
 */
export function AlertRulesPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const metric = metricOf(filterValue("metric"));
  const gapsOnly = filterValue("gaps") === "true";

  const filters: AlertRuleFilters = {
    ...(metric ? { metric } : {}),
    ...(gapsOnly ? { withoutRecipients: true } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    page,
  };

  const rules = useQuery(alertRulesQuery(client, filters));

  const rows = rules.data?.data ?? [];
  const silent = rows.filter(
    (rule) => rule.enabled && reachesNobody(rule),
  ).length;
  // Nothing filtered and still nothing back: the organization has no rules at
  // all, so the engine opens no incidents for it. Said plainly, with the
  // action that fixes it (ADR-092).
  const noRulesAtAll = rules.data?.pagination.total === 0 && !metric && !gapsOnly;
  // A super admin has no organization of their own, so the defaults need the
  // one the page is filtered to.
  const organizationFilter = filterValue("organizationId");
  const defaultsTarget = isSuperAdmin
    ? organizationFilter
      ? { organizationId: organizationFilter }
      : null
    : {};

  const createDefaults = useMutation({
    ...createDefaultAlertRulesMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
    },
  });
  // org? + metric + applies + range + duration + cooldown + severity + recipients + status + actions
  const columnCount = isSuperAdmin ? 10 : 9;

  return (
    <>
      <PageHeader
        title={t("alertRulesTitle")}
        description={t("alertRulesDescription")}
        action={
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("newRule")}
          </Button>
        }
      />

      {isSuperAdmin ? (
        <>
          <TestIncidentEmailCard />
          <TestIncidentWhatsAppCard />
        </>
      ) : null}

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              label={tCommon("metric")}
              value={filterValue("metric")}
              onChange={(value) => setFilter("metric", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...ALERT_METRICS.map((value) => ({
                  value,
                  label: ALERT_METRIC_LABELS[value],
                })),
              ]}
            />

            <SelectField
              label={tCommon("recipients")}
              value={gapsOnly ? "true" : ""}
              onChange={(value) => setFilter("gaps", value)}
              data={[
                { value: "", label: t("recipientsAllRules") },
                { value: "true", label: t("recipientsWithout") },
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {rules.isPending ? <LoadingBlock label={t("loadingAlertRules")} /> : null}

      {rules.isError ? (
        <ErrorBlock error={rules.error} onRetry={() => void rules.refetch()} />
      ) : null}

      {noRulesAtAll ? (
        <Alert tone="warn" title={t("emptyAlertRulesNoneTitle")}>
          <p>{t("emptyAlertRulesNoneBody")}</p>
          {defaultsTarget === null ? (
            <p className="mt-2">{t("emptyAlertRulesPickOrganization")}</p>
          ) : (
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                loading={createDefaults.isPending}
                onClick={() => createDefaults.mutate(defaultsTarget)}
              >
                {t("createDefaultRules")}
              </Button>
            </div>
          )}
          {createDefaults.isError ? (
            <p className="mt-2 text-danger">{createDefaults.error.message}</p>
          ) : null}
        </Alert>
      ) : null}

      {rules.data && !noRulesAtAll ? (
        <>
          {silent > 0 && !gapsOnly ? (
            <Card className="mb-4">
              <CardBody>
                <p className="text-sm">
                  <span className="font-semibold">
                    {silent === 1 ? t("silentRulesOne") : t("silentRulesMany", { count: silent })}
                  </span>{" "}
                  {t("silentRulesBody")}{" "}
                  <button
                    type="button"
                    onClick={() => setFilter("gaps", "true")}
                    className="text-brand underline underline-offset-2"
                  >
                    {t("showJustThose")}
                  </button>
                </p>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <DataTable caption={t("alertRulesCaption")}>
              <thead>
                <tr>
                  {isSuperAdmin ? <Th>{tCommon("organization")}</Th> : null}
                  <Th>{t("colMetric")}</Th>
                  <Th>{t("colAppliesTo")}</Th>
                  <Th>{t("colRange")}</Th>
                  <Th>{t("colMustHoldFor")}</Th>
                  <Th>{t("colCooldown")}</Th>
                  <Th>{tCommon("severity")}</Th>
                  <Th>{t("colRecipients")}</Th>
                  <Th>{tCommon("status")}</Th>
                  <Th>{tCommon("actions")}</Th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <EmptyRow colSpan={columnCount}>
                    {gapsOnly ? t("emptyAlertRulesClear") : t("emptyAlertRulesFiltered")}
                  </EmptyRow>
                ) : null}

                {rows.map((rule) => (
                  <tr key={rule.id}>
                    {isSuperAdmin ? <Td>{rule.organizationName}</Td> : null}
                    <Td className="font-medium">{ALERT_METRIC_LABELS[rule.metric]}</Td>
                    <Td>
                      {rule.deviceId === null ? (
                        <span className="text-ink-muted">{t("appliesEveryDevice")}</span>
                      ) : (
                        (rule.assetName ?? rule.devEui)
                      )}
                    </Td>
                    <Td className="tabular-nums">
                      {formatBounds(rule.metric, rule.minValue, rule.maxValue)}
                    </Td>
                    {metricHasRuleTimers(rule.metric) ? (
                      <>
                        <Td className="tabular-nums">{formatDuration(rule.durationSeconds)}</Td>
                        <Td className="tabular-nums">{formatDuration(rule.cooldownSeconds)}</Td>
                      </>
                    ) : (
                      <>
                        <Td className="text-ink-muted">{t("timersFromChannel")}</Td>
                        <Td className="text-ink-muted">{t("timersFromChannel")}</Td>
                      </>
                    )}
                    <Td>
                      <SeverityBadge value={rule.severity} />
                    </Td>
                    <Td>
                      <RecipientsSummary rule={rule} />
                    </Td>
                    <Td>
                      {rule.enabled ? (
                        <Badge tone="ok">{t("enabled")}</Badge>
                      ) : (
                        <Badge tone="neutral">{t("disabled")}</Badge>
                      )}
                    </Td>
                    <Td>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(rule)}>
                        {t("editRule")}
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr>
                  <Td colSpan={columnCount} className="border-b-0">
                    <Pagination
                      page={rules.data.pagination.page}
                      totalPages={rules.data.pagination.totalPages}
                      total={rules.data.pagination.total}
                      noun={{ one: tCommon("noun.rule.one"), other: tCommon("noun.rule.other") }}
                      onChange={setPage}
                    />
                  </Td>
                </tr>
              </tfoot>
            </DataTable>
          </Card>
        </>
      ) : null}

      {editing ? <EditAlertRuleDialog rule={editing} onClose={() => setEditing(null)} /> : null}

      {creating ? (
        <CreateAlertRuleDialog
          {...(organizationFilter ? { organizationId: organizationFilter } : {})}
          onClose={() => setCreating(false)}
        />
      ) : null}
    </>
  );
}

function reachesNobody(rule: AlertRule): boolean {
  return (
    rule.recipients.length === 0 &&
    rule.whatsappContactIds.length === 0 &&
    rule.voiceContactIds.length === 0
  );
}

function RecipientsSummary({ rule }: { rule: AlertRule }) {
  const { t } = useTranslation("pages");

  if (reachesNobody(rule)) {
    return <Badge tone="warn">{t("nobody")}</Badge>;
  }

  const channels = [
    rule.whatsappContactIds.length > 0 ? t("channelWhatsapp") : null,
    rule.voiceContactIds.length > 0 ? t("channelVoice") : null,
  ].filter((label) => label !== null);

  if (rule.recipients.length === 0) {
    return <span>{channels.join(", ")}</span>;
  }

  const title = [...rule.recipients, ...channels].join(", ");

  return (
    <span className="truncate" title={title}>
      {rule.recipients[0]}
      {rule.recipients.length > 1 ? (
        <span className="text-ink-muted"> +{rule.recipients.length - 1}</span>
      ) : null}
      {channels.length > 0 ? <span className="text-ink-muted"> · {channels.join(", ")}</span> : null}
    </span>
  );
}
